/**
 * Record every confirmed quote send (Quote Brain slice K3).
 *
 * When the operator confirms "Yes, I sent it" after the email hand-off, this
 * posts the quote exactly as Stage 5 rendered it to POST
 * /api/quotes/artifacts/sent, which writes the sent-quote snapshot, its
 * delivery event and the corpus row in one transaction.
 *
 * NEVER BLOCKS THE SEND. The email has already gone from the operator's own
 * mail client by the time this runs. A failed write is kept in a per-business
 * outbox in localStorage and retried (5 s, 30 s, 2 min, 10 min, then every
 * 15 min while the app is open, and again on the next start). Nothing is
 * silent: the first failure, a refusal and a late success each raise a
 * window event App turns into a toast.
 *
 * Retries resend the identical body, quoteRef included. The backend is
 * idempotent on quoteRef, so a retry after a lost response returns the
 * original record instead of writing a second one.
 */

import { mintQuoteRef } from './artifact-stamp';
import { recordSentQuote, type RecordSentQuoteInput, type RecordSentQuoteResult } from './artifacts-client';
import { getStoredTokenClaims, stripInlineCustomerMediaForWire } from '../data-service-api.js';
import { inferJobTypeSlug, measureOperatorAuthorship } from '../quote-corpus-nomination.js';
import { APP_VERSION } from '../../config/brand.js';

export const SENT_QUOTE_RECORD_EVENT = 'qm:sent-quote-record';

export interface SentQuoteRecordEventDetail {
    status: 'recorded' | 'retrying' | 'failed';
    message: string;
    quoteRef: string | null;
    projectId: string | null;
}

interface OutboxEntry {
    input: RecordSentQuoteInput;
    attempts: number;
    lastError: string | null;
}

type Outbox = Record<string, OutboxEntry>;

const STORAGE_PREFIX = 'qm.sentQuoteOutbox.v1.';
const RETRY_DELAYS_MS = [5_000, 30_000, 120_000, 600_000];
const STEADY_RETRY_MS = 15 * 60_000;

const memoryOutboxes = new Map<string, Outbox>();
const inFlight = new Set<string>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function outboxKey(): string | null {
    const tenantId = getStoredTokenClaims()?.tenantId;
    return tenantId ? `${STORAGE_PREFIX}${tenantId}` : null;
}

function readOutbox(key: string): Outbox {
    try {
        const raw = globalThis.localStorage?.getItem(key);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Outbox;
        }
    } catch { /* storage unavailable: the memory copy below still holds this tab's entries */ }
    return { ...(memoryOutboxes.get(key) || {}) };
}

function writeOutbox(key: string, outbox: Outbox): void {
    memoryOutboxes.set(key, outbox);
    try {
        if (Object.keys(outbox).length === 0) globalThis.localStorage?.removeItem(key);
        else globalThis.localStorage?.setItem(key, JSON.stringify(outbox));
    } catch { /* quota or privacy mode: this tab keeps retrying from memory */ }
}

function emit(detail: SentQuoteRecordEventDetail): void {
    try {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(SENT_QUOTE_RECORD_EVENT, { detail }));
        }
    } catch (err) {
        console.warn('[QM] Sent-quote record event dispatch failed:', err);
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const out = String(value).trim();
    return out || null;
}

/**
 * A refusal the same body can never get past. Everything else (network,
 * 5xx, auth refresh, a project the server has not got yet) is retried.
 */
function isPermanentFailure(err: unknown): boolean {
    const status = Number((err as { status?: unknown } | null)?.status);
    const code = String((err as { code?: unknown } | null)?.code || '');
    if (code === 'QUOTE_SENT_PROJECT_NOT_FOUND') return false;
    return status === 400 || status === 409 || status === 413 || status === 422;
}

function failureText(err: unknown): string {
    const e = err as { message?: unknown; detail?: unknown } | null;
    return [text(e?.message), text(e?.detail)].filter(Boolean).join(' ') || 'unknown error';
}

export interface BuildSentQuoteRecordArgs {
    projectId: string;
    /** The snapshot Stage 5 captured at the send confirmation. */
    quoteSnapshot: unknown;
    formData?: unknown;
    /** EmailQuoteModal's sent_confirmed telemetry detail. */
    emailDetail: Record<string, unknown>;
    quoteRef?: string;
}

/** The request body for one confirmed send, or null when there is nothing to record. */
export function buildSentQuoteRecord({
    projectId,
    quoteSnapshot,
    formData = null,
    emailDetail,
    quoteRef = mintQuoteRef(),
}: BuildSentQuoteRecordArgs): RecordSentQuoteInput | null {
    if (!text(projectId) || !isRecord(quoteSnapshot)) return null;
    const quoteData = stripInlineCustomerMediaForWire(quoteSnapshot);
    if (!isRecord(quoteData)) return null;
    const scope = Array.isArray(quoteData.scope) ? quoteData.scope : [];
    const header = isRecord(quoteData.header) ? quoteData.header : {};
    const authorship = measureOperatorAuthorship({ quoteData });
    const sentConfirmedAt = text(emailDetail.sentConfirmedAt) || text(emailDetail.timestamp) || new Date().toISOString();
    const cleanForm = isRecord(formData) ? stripInlineCustomerMediaForWire(formData) : null;
    return {
        quoteRef,
        projectId: String(projectId),
        quoteData,
        formData: isRecord(cleanForm) ? cleanForm : null,
        sentConfirmedAt,
        draftOpenedAt: text(emailDetail.draftOpenedAt),
        delivery: {
            mode: text(emailDetail.mode),
            to: text(emailDetail.recipient) || '',
            cc: text(emailDetail.cc),
            subject: text(emailDetail.subject),
            pdfFilename: text(emailDetail.pdf_filename),
        },
        corpus: {
            jobType: inferJobTypeSlug(scope),
            summary: (text(quoteData.scopeObjective) || text(header.title) || '').slice(0, 200) || null,
            workTypeTags: [...new Set(scope
                .map((item: any) => item?.workTypeSlug || item?.work_type_slug || item?.category)
                .filter((value: unknown): value is string => typeof value === 'string' && value.trim().length > 0))],
            flags: [
                authorship.operatorAuthored ? 'voice:operator' : 'voice:generator',
                `edited:${authorship.edited}/${authorship.total}`,
                'source:send-confirmation',
            ],
        },
        appVersion: APP_VERSION,
    };
}

function scheduleRetry(key: string, quoteRef: string, attempts: number): void {
    const existing = timers.get(quoteRef);
    if (existing) clearTimeout(existing);
    const delay = RETRY_DELAYS_MS[attempts - 1] ?? STEADY_RETRY_MS;
    timers.set(quoteRef, setTimeout(() => {
        timers.delete(quoteRef);
        void attempt(key, quoteRef);
    }, delay));
}

async function attempt(key: string, quoteRef: string): Promise<RecordSentQuoteResult | null> {
    if (inFlight.has(quoteRef)) return null;
    const entry = readOutbox(key)[quoteRef];
    if (!entry) return null;
    inFlight.add(quoteRef);
    try {
        const result = await recordSentQuote(entry.input);
        const outbox = readOutbox(key);
        delete outbox[quoteRef];
        writeOutbox(key, outbox);
        console.log(`[QM] Sent quote recorded: ${result.quoteRef} artifact=${result.artifactId} corpus=${result.corpusStatus ?? result.corpusSkippedReason}`);
        if (result.corpusSkippedReason) {
            emit({
                status: 'recorded',
                message: 'The sent quote is recorded, but it could not be added to past quotes.',
                quoteRef,
                projectId: entry.input.projectId,
            });
        } else if (entry.attempts > 0) {
            emit({ status: 'recorded', message: 'The sent quote is now recorded.', quoteRef, projectId: entry.input.projectId });
        }
        return result;
    } catch (err) {
        const reason = failureText(err);
        const outbox = readOutbox(key);
        if (isPermanentFailure(err)) {
            delete outbox[quoteRef];
            writeOutbox(key, outbox);
            console.warn(`[QM] Sent quote ${quoteRef} was refused and will not be retried:`, reason);
            emit({
                status: 'failed',
                message: `QuoteMaker could not record this sent quote: ${reason}`,
                quoteRef,
                projectId: entry.input.projectId,
            });
            return null;
        }
        const current = outbox[quoteRef] || entry;
        const attempts = current.attempts + 1;
        outbox[quoteRef] = { ...current, attempts, lastError: reason };
        writeOutbox(key, outbox);
        console.warn(`[QM] Recording sent quote ${quoteRef} failed (attempt ${attempts}); retrying:`, reason);
        if (attempts === 1) {
            emit({
                status: 'retrying',
                message: 'The email went, but QuoteMaker could not record the sent quote yet. It will keep trying.',
                quoteRef,
                projectId: entry.input.projectId,
            });
        }
        scheduleRetry(key, quoteRef, attempts);
        return null;
    } finally {
        inFlight.delete(quoteRef);
    }
}

/**
 * Record one confirmed send. Resolves once the first attempt settles and never
 * rejects: failures stay in the outbox and are retried.
 */
export async function recordQuoteSent(args: BuildSentQuoteRecordArgs): Promise<RecordSentQuoteResult | null> {
    try {
        const input = buildSentQuoteRecord(args);
        if (!input) {
            console.warn('[QM] Sent quote not recorded: no project or quote snapshot at the send confirmation.');
            emit({
                status: 'failed',
                message: 'QuoteMaker could not record this sent quote: the quote was not available at the send confirmation.',
                quoteRef: null,
                projectId: text(args.projectId),
            });
            return null;
        }
        const key = outboxKey();
        if (!key) {
            emit({
                status: 'failed',
                message: 'QuoteMaker could not record this sent quote: you are signed out.',
                quoteRef: input.quoteRef,
                projectId: input.projectId,
            });
            return null;
        }
        const outbox = readOutbox(key);
        outbox[input.quoteRef] = { input, attempts: 0, lastError: null };
        writeOutbox(key, outbox);
        return await attempt(key, input.quoteRef);
    } catch (err) {
        console.warn('[QM] Sent quote recording crashed:', err);
        return null;
    }
}

/** Retry whatever this business's outbox holds. Returns the stop function. */
export function startSentQuoteRecorder(): () => void {
    const drain = () => {
        const key = outboxKey();
        if (!key) return;
        for (const quoteRef of Object.keys(readOutbox(key))) void attempt(key, quoteRef);
    };
    const key = outboxKey();
    const pending = key ? Object.keys(readOutbox(key)).length : 0;
    if (pending > 0) {
        emit({
            status: 'retrying',
            message: `${pending} sent quote${pending === 1 ? ' is' : 's are'} not recorded yet. QuoteMaker is retrying.`,
            quoteRef: null,
            projectId: null,
        });
    }
    drain();
    const interval = setInterval(drain, STEADY_RETRY_MS);
    const onOnline = () => drain();
    if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
    return () => {
        clearInterval(interval);
        if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
        for (const timer of timers.values()) clearTimeout(timer);
        timers.clear();
    };
}

export const __testHooks = { outboxKey, readOutbox, RETRY_DELAYS_MS, STEADY_RETRY_MS };
